import { motion } from 'framer-motion'
import { Avatar } from 'antd'
import Link from 'next/link'
import { useRecoilValue } from 'recoil'
import { userState } from 'Store/user'
import CopyMessage from '@/app/[lang]/@desktop/_components/CopyMessage'
import { getSelfCharacterList } from 'Apis/character'
import { getSelfPlotList } from 'Apis/plot'
import useSWR from 'swr'
import { BASE_API_URL } from '@/constant/api'
import { useEffect, useState } from 'react'
import { ReviewStage } from 'Types/review'
import { DefaultAvatar } from '../svg/DefaultAvatar'
import { transformImage } from '@/utils/img'
import { SMALL_IMG_WIDTH } from '@/constant/img'
import { useRouter } from 'next/navigation'
import useUserWallet from '@/hooks/swr/useUserWallet'
import { formatMessageAmount } from '@/utils/subscribe'
import { isAngelUser } from '@/utils/admin'
import { BetaSupporterAvatarFrame } from 'Comps/svg/BetaSupporterAvatarFrame'
import useI18n from 'Hooks/useI18n'
import {
  CHRISTMAS_DEADLINE_TIME,
  HALLOWEEN_2025_DEADLINE_TIME,
  CHRISTMAS_2025_DEADLINE_TIME,
} from '@/constant/date'
import useCountdown from '@/hooks/useCountdown'
import { ChristmasAvatarFrame } from 'Comps/svg/ChristmasAvatarFrame'
import Christmas2025AvatarFrame from '../svg/Christmas2025AvatarFrame'
import useUserSubscription from '@/hooks/swr/useUserSubscription'
import {
  MemberShipTierName,
  PatreonUnsubscribeLink,
  SubscribeStarUnsubscribeLink,
} from '@/constant/subscribe'
import {
  AutoRenewStatus,
  SubscriptionStatus,
  PaymentProvider,
  PlanCycle,
} from '@/types/subscribe'
import {
  aiglobalpaySubscribeCancel,
  paypalSubscribeCancel,
  stripeSubscribeCancel,
} from '@/apis/subscribe'
import { LoadingOutlined } from '@ant-design/icons'
import DeleteAccount from '../modal/DeleteAccount'
import TipsyModal from '../modal/TipsyModal'
import { withLocale } from '@/locales'
import UnsubscribeFeedbackModal from '@/components-mobile/subscribe/UnsubscribeFeedbackModal'
import Halloween2025AvatarFrame from '../svg/Halloween2025AvatarFrame'
import BlacklistModal from './BlacklistModal'
import { homeNSFWState } from '@/store/home'
type UserInfoProps = {
  onClose: () => void
  showLogout: () => void
}
export default function UserInfo({ onClose, showLogout }: UserInfoProps) {
  const { t, lang } = useI18n()
  const router = useRouter()
  const user = useRecoilValue(userState)
  const nsfw = useRecoilValue(homeNSFWState)
  const { isExpired } = useCountdown(CHRISTMAS_DEADLINE_TIME)
  const { isExpired: isChristmas2025Expired } = useCountdown(
    CHRISTMAS_2025_DEADLINE_TIME
  )
  const { isExpired: isHalloweenExpired } = useCountdown(
    HALLOWEEN_2025_DEADLINE_TIME
  )
  const { userSubscription, mutate } = useUserSubscription()
  const [paypalModalOpen, setPaypalModalOpen] = useState(false)
  const [appStoreModalOpen, setAppStoreModalOpen] = useState(false)
  const [aiglobalpayModalOpen, setAiglobalpayModalOpen] = useState(false)
  const [stripeModalOpen, setStripeModalOpen] = useState(false)
  const [isUnsubscribing, setIsUnsubscribing] = useState(false)
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false)
  const [blacklistModalOpen, setBlacklistModalOpen] = useState(false)
  const {
    userWalletInfo: { totalMsgAmount },
  } = useUserWallet()

  const handleClickLogout = () => {
    onClose()
    showLogout()
  }

  const {
    data: charData,
    error: charError,
    isLoading: charLoading,
  } = useSWR(`${BASE_API_URL}/character/list/self?lang=${lang}`, () =>
    getSelfCharacterList({
      page: 0,
      size: 1,
      review_stage: 'all',
      language_code: lang,
    })
  )
  const {
    data: memoData,
    error: memoError,
    isLoading: memoLoading,
  } = useSWR(`${BASE_API_URL}/plot/list/self`, () =>
    getSelfPlotList({
      page: 0,
      size: 1,
      review_stage: ReviewStage.All,
      nsfw: nsfw,
    })
  )

  useEffect(() => {
    if (charError || memoError) {
      console.error('useSWR Error:', charError)
      console.error('useSWR Error:', memoError)
    }
  }, [charError, memoError])

  const handleUnSubscribe = () => {
    if (userSubscription?.payment_provider_id === PaymentProvider.Patreon) {
      window.open(PatreonUnsubscribeLink, '_blank')
    } else if (
      userSubscription?.payment_provider_id === PaymentProvider.SStar
    ) {
      window.open(SubscribeStarUnsubscribeLink, '_blank')
    } else if (
      userSubscription?.payment_provider_id === PaymentProvider.Paypal
    ) {
      setPaypalModalOpen(true)
    } else if (
      userSubscription?.payment_provider_id === PaymentProvider.AppStore
    ) {
      setAppStoreModalOpen(true)
    } else if (
      userSubscription?.payment_provider_id === PaymentProvider.AIglobalPay
    ) {
      setAiglobalpayModalOpen(true)
    } else if (
      userSubscription?.payment_provider_id === PaymentProvider.Stripe
    ) {
      setStripeModalOpen(true)
    }
  }

  return (
    <motion.div
      className="pointer-events-auto absolute right-0 w-[245px] rounded-[10px] bg-secondary-bg p-2.5 text-white opacity-80"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        background:
          'linear-gradient(180deg, rgba(24, 14, 31, 0.8) 0%, rgba(65, 35, 77, 0.8) 50%, rgba(29, 21, 21, 0.8) 100%)',
      }}
    >
      <Link
        href={withLocale(`/profile/${user?.user_id}`, lang)}
        prefetch
        className="flex items-center gap-2.5"
      >
        <div
          className="avatar flex-shrink-0 rounded-[18px] border border-secondary-border p-1"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(87,47,102,0.6) 50%, rgba(29,21,21,0.6) 100%)',
          }}
        >
          <div className="relative rounded-full border border-white/50">
            <Avatar
              src={
                user?.avatar_url
                  ? transformImage(user.avatar_url, SMALL_IMG_WIDTH)
                  : ''
              }
              alt=""
              size={50}
              style={{ border: 'none' }}
              icon={<DefaultAvatar />}
            />
            {isAngelUser(user?.user_id) &&
              isExpired &&
              isHalloweenExpired &&
              isChristmas2025Expired && (
                <BetaSupporterAvatarFrame
                  idPrefix="user-info"
                  size={65}
                  style={{
                    position: 'absolute',
                    bottom: '-10px',
                    left: '-7px',
                  }}
                />
              )}
            {!isExpired && (
              <ChristmasAvatarFrame
                idPrefix="user-info"
                style={{ position: 'absolute', top: '-19px', right: '-12px' }}
              />
            )}

            {!isHalloweenExpired && (
              <Halloween2025AvatarFrame
                style={{
                  position: 'absolute',
                  top: '-1px',
                  right: '-1px',
                }}
              />
            )}
            {!isChristmas2025Expired && (
              <Christmas2025AvatarFrame
                avatarSize={50}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />
            )}
          </div>
        </div>
        <div>
          <div className="w-[140px] truncate text-base font-medium text-white">
            {user?.nickname}
          </div>
          <div className="flex text-sm text-white/70">
            {/* i18n-ignore */}
            UID:
            <div className="w-[100px] truncate">{user?.user_id}</div>
            <CopyMessage message={user?.user_id || ''} />
          </div>
        </div>
      </Link>
      <div className="mt-2.5 grid h-[40px] grid-cols-1">
        <div className="flex h-full items-center justify-center px-[18px] pr-[15px] text-white">
          <span className="flex max-w-[180px] items-center break-all text-center">
            {user?.email || t('Not Available Email')}
          </span>
        </div>
      </div>
      <div className="mt-2.5 grid grid-cols-1 rounded-lg border border-solid border-white/20">
        {/* TODO: 个人余额 */}
        <div
          onClick={() => router.push(withLocale('/balance', lang))}
          className="inset-0 flex h-full cursor-pointer items-center justify-between gap-[10px] rounded-t-lg px-[18px] py-1 pr-[15px] text-white hover:bg-white/10"
        >
          <span className="min-w-0 flex-1 text-sm">{t('My Balance')}</span>
          <span className="ml-2 flex-none whitespace-nowrap text-end">
            {formatMessageAmount(t, totalMsgAmount)}
          </span>
        </div>

        <Link
          href={withLocale(`/profile/${user?.user_id}?tab=Characters`, lang)}
          prefetch
        >
          <div className="inset-0 flex h-full items-center justify-between gap-[10px] px-[18px] py-1 pr-[15px] text-white hover:bg-white/10">
            <span className="min-w-0 flex-1 text-sm">{t('Characters')}</span>
            <span className="ml-2 inline-flex h-[19px] w-[36px] flex-none items-center justify-end whitespace-nowrap text-end leading-[19px]">
              {charLoading ? (
                <div className="relative h-[19px] w-[36px] overflow-hidden rounded-full bg-white/20">
                  <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                </div>
              ) : (
                <span>{charData?.total_characters || 0}</span>
              )}
            </span>
          </div>
        </Link>

        <Link
          href={withLocale(`/profile/${user?.user_id}?tab=Memories`, lang)}
          prefetch
        >
          <div className="inset-0 flex h-full items-center justify-between gap-[10px] px-[18px] py-1 pr-[15px] text-white hover:bg-white/10">
            <span className="min-w-0 flex-1 text-sm">{t('Memories')}</span>
            <span className="ml-2 inline-flex h-[19px] w-[36px] flex-none items-center justify-end whitespace-nowrap text-end leading-[19px]">
              {memoLoading ? (
                <div className="relative h-[19px] w-[36px] overflow-hidden rounded-full bg-white/20">
                  <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                </div>
              ) : (
                <span>{(memoData && memoData[0]?.total) || 0}</span>
              )}
            </span>
          </div>
        </Link>

        {userSubscription.plan_cycle === PlanCycle.Monthly &&
        userSubscription?.status === SubscriptionStatus.Active &&
        userSubscription.auto_renew_status === AutoRenewStatus.On ? (
          <div
            className="inset-0 flex h-full cursor-pointer items-center justify-between gap-[10px] px-[18px] py-1 pr-[15px] text-white hover:bg-white/10"
            onClick={handleUnSubscribe}
          >
            <span className="w-3/4 break-words text-sm">
              {t('Unsubscribe')}
            </span>
          </div>
        ) : (
          <Link href={withLocale(`/billing/plans`, lang)} prefetch>
            <div className="inset-0 flex h-full items-center justify-between gap-[10px] px-[18px] py-1 pr-[15px] text-white hover:bg-white/10">
              <span className="min-w-0 flex-1 text-start text-sm">
                {t('My Plan')}
              </span>
              <span className="ml-2 flex-none whitespace-nowrap text-end">
                {t(MemberShipTierName[userSubscription?.plan_id || 0])}
              </span>
            </div>
          </Link>
        )}
        <div
          className="inset-0 flex h-full cursor-pointer items-center justify-between gap-[10px] px-[18px] py-1 pr-[15px] text-white hover:bg-white/10"
          onClick={() => setBlacklistModalOpen(true)}
        >
          <span className="min-w-0 flex-1 text-sm">{t('Blocked')}</span>
        </div>
        <div
          className="inset-0 flex h-full cursor-pointer items-center justify-between gap-[10px] rounded-b-lg px-[18px] py-1 pr-[15px] text-white hover:bg-white/10"
          onClick={() => setDeleteAccountModalOpen(true)}
        >
          <span className="min-w-0 flex-1 text-sm">{t('Delete Account')}</span>
        </div>
        <BlacklistModal
          open={blacklistModalOpen}
          onClose={() => setBlacklistModalOpen(false)}
        />
        <DeleteAccount
          open={deleteAccountModalOpen}
          onCancel={() => setDeleteAccountModalOpen(false)}
        />
        <TipsyModal
          open={appStoreModalOpen}
          onClose={() => setAppStoreModalOpen(false)}
          onConfirm={() => setAppStoreModalOpen(false)}
          title=""
          desc={
            <div className="text-start">
              <p>{t('How to cancel your subscription?')}</p>
              <p>{t('1. Open the "Settings" app on your iPhone or iPad.')}</p>
              <p>{t('2. Tap your Apple ID at the top.')}</p>
              <p>{t('3. Select "Subscriptions".')}</p>
              <p>{t('4. Find and tap the subscription you want to cancel.')}</p>
              <p>{t('5. Tap "Cancel Subscription" and confirm.')}</p>
            </div>
          }
          cancelText={t('Got it')}
        />
        <TipsyModal
          open={aiglobalpayModalOpen}
          onClose={() => setAiglobalpayModalOpen(false)}
          onConfirm={async () => {
            setIsUnsubscribing(true)
            try {
              await aiglobalpaySubscribeCancel()
              mutate()
            } catch (error) {
              console.error(error)
            } finally {
              setIsUnsubscribing(false)
              setAiglobalpayModalOpen(false)
            }
          }}
          title=""
          desc={t(
            "Are you sure you want to unsubscribe? Your subscription will not be renewed, but you will retain access to this month's services."
          )}
          cancelText={t('Cancel')}
          confirmText={
            isUnsubscribing ? (
              <LoadingOutlined style={{ color: 'white' }} />
            ) : (
              t('Confirm')
            )
          }
        />
        <UnsubscribeFeedbackModal
          open={paypalModalOpen}
          onClose={() => setPaypalModalOpen(false)}
          cancelFunc={paypalSubscribeCancel}
          width={450}
        />
        <UnsubscribeFeedbackModal
          open={stripeModalOpen}
          onClose={() => setStripeModalOpen(false)}
          cancelFunc={stripeSubscribeCancel}
          width={450}
        />
      </div>
      <div
        className="mt-2 h-[54px] rounded-lg px-[18px] text-sm leading-[54px] hover:bg-white/10"
        onClick={handleClickLogout}
      >
        {t('Log out')}
      </div>
    </motion.div>
  )
}
